import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog, FormHelperText, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import CLOSE_ICON from '../../assets/svg/close-icon.svg';
import { LANG_TEXT } from '../../constants/language';
import { PreorderRuleState } from '../../pages/PreoderRules/PreorderRules.types';
import { getPreorderRuleValidationMessage, validatePreorderRuleAgainstMaster } from '../../utils/preorderRules';
import Button from '../Button/Button';
import Input from '../Input/Input';
import { cancelButtonStyle, confirmButtonStyle } from '../Modal/styles';
import TimeUnitInput from '../TimeUnitInput/TimeUnitInput';
import { useStyles } from './styles';

interface PreOrderFormData {
  emails: string;
}

export interface PreOrderModalSubmitData extends PreOrderFormData {
  preorderRule: PreorderRuleState;
}

interface PreOrderModalProps {
  open: boolean;
  handleClose: () => void;
  handleSubmit: (formData: PreOrderModalSubmitData) => Promise<void>;
  initialRule?: PreorderRuleState;
  initialEmails?: string;
  masterRule?: PreorderRuleState;
}

const fallbackRule: PreorderRuleState = {
  acceptanceSince: { value: 30, unit: 'day', pickerValue: '00:00' },
  acceptanceUntil: { value: 0, unit: 'day', pickerValue: '00:00' },
  orderDeliverableUntil: { value: 2, unit: 'hour' },
} as PreorderRuleState;

const PreOrderModal: React.FC<PreOrderModalProps> = ({ open, handleClose, handleSubmit, initialRule, initialEmails, masterRule }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [ruleError, setRuleError] = useState<string>('');
  const [resetKey, setResetKey] = useState(0);
  const activeRule = initialRule || fallbackRule;
  const preorderRuleRef = useRef<PreorderRuleState>(activeRule);
  const acceptanceInputLimit = { min: 0, max: 9999 };

  React.useEffect(() => {
    if (open) {
      const rule = initialRule || fallbackRule;
      preorderRuleRef.current = rule;
      setRuleError('');
      setResetKey((k) => k + 1);
      reset({ emails: initialEmails || '' });
    }
  }, [open, initialRule, initialEmails]);

  const validationSchema = yup.object({
    emails: yup
      .string()
      .required(t(LANG_TEXT.EMAIL_REQUIRED))
      .test('valid-emails', t(LANG_TEXT.INVALID_EMAIL), (value) => {
        if (!value) return false;
        const emails = value.split(',').map((email) => email.trim());
        return emails.every((email) => yup.string().email().isValidSync(email));
      })
      .test('max-emails', t(LANG_TEXT.MAX_EMAILS_EXCEEDED), (value) => {
        if (!value) return true;
        const emails = value.split(',').map((email) => email.trim()).filter((email) => email.length > 0);
        return emails.length <= 3;
      }),
  });

  const {
    control,
    handleSubmit: handleFormSubmit,
    formState: { errors },
    reset,
  } = useForm<PreOrderFormData>({
    defaultValues: {
      emails: '',
    },
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: PreOrderFormData) => {
    const validationError = validatePreorderRuleAgainstMaster(preorderRuleRef.current, masterRule);
    if (validationError) {
      setRuleError(t(getPreorderRuleValidationMessage(validationError)));
      return;
    }

    setIsSubmitting(true);
    try {
      await handleSubmit({ ...data, preorderRule: preorderRuleRef.current });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    reset();
    preorderRuleRef.current = activeRule;
    setRuleError('');
    setIsSubmitting(false);
    handleClose();
  };

  const clearRuleError = () => {
    setRuleError('');
  };

  return (
    <Dialog className={classes.Modal} open={open} onClose={handleModalClose} aria-labelledby="pre-order-modal-title">
      <Box className={classes.Box}>
        <Box className={classes.BoxHeader}>
          <Box className={classes.BoxTitleModal}>
            <Box className={classes.TitleModal}>
              <Typography fontSize={18} fontWeight={600} lineHeight={'28px'} color={'var(--Gray-900, #101828)'}>
                {t(LANG_TEXT.PRE_ORDER_CONFIRM)}
              </Typography>
            </Box>
            <Box className={classes.Icon} onClick={handleModalClose}>
              <img src={CLOSE_ICON} height={20} width={20} alt="CLOSE_ICON" />
            </Box>
          </Box>
        </Box>

        <Box className={classes.BoxContent}>
          <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'} mb={2}>
            {t(LANG_TEXT.PRE_ORDER_EMAIL_DESCRIPTION)}
          </Typography>
          <Typography fontSize={14} lineHeight={'18px'} color={'var(--Gray-500, #667085)'} mb={2} fontStyle="italic">
            {t(LANG_TEXT.PRE_ORDER_BULK_WARNING)}
          </Typography>

          <Input control={control as any} name="emails" placeholder="email1@example.com, email2@example.com" error={errors.emails?.message} type="text" />
          <FormHelperText>{t(LANG_TEXT.MULTIPLE_EMAILS_HINT)}</FormHelperText>

          {/* Preorder Rules Section */}
          <Box
            sx={{
              mt: 2,
              p: 2,
              border: '1px solid #E4E7EC',
              borderRadius: '8px',
              backgroundColor: '#F9FAFB',
            }}
          >
            <Typography fontSize={14} fontWeight={600} color={'#344054'} mb={1.5}>
              {t(LANG_TEXT.PREORDER_RULES)}
            </Typography>

            {/* Pre-order acceptance */}
            <Typography fontSize={13} fontWeight={500} color={'#344054'} mb={1}>
              {t(LANG_TEXT.PRE_ORDER_ACCEPTANCE)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2, overflowX: 'auto' }}>
              <TimeUnitInput
                key={`acceptance-since-${resetKey}`}
                label1={t(LANG_TEXT.SINCE)}
                label2={t(LANG_TEXT.PRE_ORDER_ACCEPTANCE_AT)}
                defaultValue={activeRule.acceptanceSince.value}
                defaultUnit={activeRule.acceptanceSince.unit}
                typeProp="time"
                defaultPickerValue={activeRule.acceptanceSince.pickerValue}
                allowMinute={false}
                allowHour={false}
                limit={acceptanceInputLimit}
                onChange={(next) => {
                  clearRuleError();
                  preorderRuleRef.current = {
                    ...preorderRuleRef.current,
                    acceptanceSince: {
                      value: next.value,
                      unit: next.unit,
                      pickerValue: next.pickerValue || preorderRuleRef.current.acceptanceSince.pickerValue,
                    },
                  };
                }}
              />
              <TimeUnitInput
                key={`acceptance-until-${resetKey}`}
                label1={t(LANG_TEXT.UNTIL)}
                label2={t(LANG_TEXT.PRE_ORDER_ACCEPTANCE_AT)}
                defaultValue={activeRule.acceptanceUntil.value}
                defaultUnit={activeRule.acceptanceUntil.unit}
                typeProp="time"
                defaultPickerValue={activeRule.acceptanceUntil.pickerValue}
                allowMinute={false}
                allowHour={false}
                limit={acceptanceInputLimit}
                onChange={(next) => {
                  clearRuleError();
                  preorderRuleRef.current = {
                    ...preorderRuleRef.current,
                    acceptanceUntil: {
                      value: next.value,
                      unit: next.unit,
                      pickerValue: next.pickerValue || preorderRuleRef.current.acceptanceUntil.pickerValue,
                    },
                  };
                }}
              />
            </Box>

            <Typography fontSize={13} fontWeight={500} color={'#344054'} mb={0.5}>
              {t(LANG_TEXT.ORDERS_DELIVERABLE)}
            </Typography>
            <Typography fontSize={12} color={'#667085'} mb={1} fontStyle="italic">
              {t(LANG_TEXT.IN_RELATION_WITH_GAME_STARTS)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflowX: 'auto' }}>
              <TimeUnitInput
                key={`order-deliverable-until-${resetKey}`}
                label1={t(LANG_TEXT.UNTIL)}
                defaultValue={activeRule.orderDeliverableUntil.value}
                defaultUnit={activeRule.orderDeliverableUntil.unit}
                typeProp="none"
                allowDay={false}
                limit={{ min: 1, max: 9999 }}
                onChange={(next) => {
                  clearRuleError();
                  preorderRuleRef.current = {
                    ...preorderRuleRef.current,
                    orderDeliverableUntil: {
                      value: next.value,
                      unit: next.unit,
                    },
                  };
                }}
              />
              <Typography fontSize="14px" fontWeight={600} color="#344054" lineHeight="20px" whiteSpace="nowrap">
                {t(LANG_TEXT.GAME_STARTS)}
              </Typography>
            </Box>
            {ruleError && <FormHelperText error>{ruleError}</FormHelperText>}
          </Box>
        </Box>

        <Box className={classes.BoxFooter}>
          <Button id="cancel_pre_order_button" fullWidth={false} title={t(LANG_TEXT.CANCEL)} buttonStyle={cancelButtonStyle} onClick={handleModalClose} />
          <Button
            id="confirm_pre_order_button"
            fullWidth={false}
            title={isSubmitting ? t(LANG_TEXT.SAVING) : t(LANG_TEXT.CONFIRM)}
            buttonStyle={confirmButtonStyle}
            disable={isSubmitting}
            onClick={handleFormSubmit(onSubmit)}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default PreOrderModal;
